// Hook pro zápis lokálního URL stavu agendy

import { useCallback } from 'react';
import { useNavigationContext } from './useNavigationContext';
import type { AgendaHandle } from '../core/agendaDefinition';
import type { ParamCodecMap, ParamInput } from '../core/paramCodec';

interface AgendaStateActions<TState extends ParamCodecMap> {
  /** Aktualizuje část stavu – používá replace */
  patch: (partial: Partial<ParamInput<TState>>) => void;
  /** Odstraní vybrané state klíče – používá replace */
  remove: (...keys: (keyof ParamInput<TState> & string)[]) => void;
  /** Vyčistí celý lokální stav – používá replace */
  reset: () => void;
}

/**
 * Hook pro zápis lokálního URL stavu agendy.
 * Změny stavu vždy používají replace, ne push.
 */
export function useAgendaState<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
  _agenda: AgendaHandle<TOpen, TState>,
  layerId: string
): AgendaStateActions<TState> {
  const { kernel } = useNavigationContext();

  const patch = useCallback(
    (partial: Partial<ParamInput<TState>>) => {
      kernel.patchLayerState(layerId, partial as Record<string, unknown>);
    },
    [kernel, layerId]
  );

  const remove = useCallback(
    (...keys: string[]) => {
      kernel.removeLayerStateKeys(layerId, keys);
    },
    [kernel, layerId]
  );

  const reset = useCallback(() => {
    kernel.resetLayerState(layerId);
  }, [kernel, layerId]);

  return { patch, remove, reset };
}
